/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode, STUDY_VOCABULARY_BOOK_LEVELS, TEST_VOCABULARY_BOOK_LEVELS, TVocabularyBookCategory, TVocabularyBookLevel } from '@datatype/iyagigo/api-type';
import { VOCABULARY_BOOK_LEVEL_LABEL } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import Chip from '@design-system/chip';

interface IProps {
  languageCode: EApiLanguageCode | null;
  category: TVocabularyBookCategory | null;
  levels: TVocabularyBookLevel[] | null;
  published: boolean | null;
  handleLanguageChange: (languageCode: EApiLanguageCode | null) => void;
  handleCategoryChange: (category: TVocabularyBookCategory | null) => void;
  handleLevelsChange: (levels: TVocabularyBookLevel[] | null) => void;
  handlePublishedChange: (published: boolean | null) => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 8px;
  align-items: center;
`;

const Label = styled.div`
  min-width: 56px;
`;

const VocabularyBookFilter: React.FC<IProps> = ({
  languageCode,
  category,
  levels,
  published,
  handleLanguageChange,
  handleCategoryChange,
  handleLevelsChange,
  handlePublishedChange,
}) => {
  const handleLevelSelect = (level: TVocabularyBookLevel) => {
    const current = levels ?? [];
    if (current.length === 1 && current[0] === level) {
      handleLevelsChange(null);
    } else {
      handleLevelsChange([level]);
    }
  };

  const levelOptions: TVocabularyBookLevel[] = (() => {
    if (category === 'TEST') return TEST_VOCABULARY_BOOK_LEVELS;
    if (category === 'STUDY') return STUDY_VOCABULARY_BOOK_LEVELS;
    return [...TEST_VOCABULARY_BOOK_LEVELS, ...STUDY_VOCABULARY_BOOK_LEVELS];
  })();

  return (
    <Wrapper>
      <Row>
        <Label>
          <TextBlock text='언어' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
        </Label>
        <Chip text='전체' size='small' isSelected={languageCode === null} handleClick={() => handleLanguageChange(null)} theme={ETheme.BROWN} />
        <Chip text='EN' size='small' isSelected={languageCode === EApiLanguageCode.ENGLISH} handleClick={() => handleLanguageChange(EApiLanguageCode.ENGLISH)} theme={ETheme.BROWN} />
        <Chip text='JA' size='small' isSelected={languageCode === EApiLanguageCode.JAPANESE} handleClick={() => handleLanguageChange(EApiLanguageCode.JAPANESE)} theme={ETheme.BROWN} />
      </Row>
      <Row>
        <Label>
          <TextBlock text='카테고리' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
        </Label>
        <Chip
          text='전체'
          size='small'
          isSelected={category === null}
          handleClick={() => {
            handleCategoryChange(null);
            handleLevelsChange(null);
          }}
          theme={ETheme.BROWN}
        />
        <Chip
          text='시험준비'
          size='small'
          isSelected={category === 'TEST'}
          handleClick={() => {
            handleCategoryChange('TEST');
            handleLevelsChange(null);
          }}
          theme={ETheme.BROWN}
        />
        <Chip
          text='일반학습'
          size='small'
          isSelected={category === 'STUDY'}
          handleClick={() => {
            handleCategoryChange('STUDY');
            handleLevelsChange(null);
          }}
          theme={ETheme.BROWN}
        />
      </Row>
      <Row>
        <Label>
          <TextBlock text='레벨' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
        </Label>
        <Chip
          text='전체'
          size='small'
          isSelected={levels === null || levels.length === 0}
          handleClick={() => handleLevelsChange(null)}
          theme={ETheme.BROWN}
        />
        {levelOptions.map((lv) => (
          <Chip
            key={lv}
            text={VOCABULARY_BOOK_LEVEL_LABEL[lv]}
            size='small'
            isSelected={(levels ?? []).includes(lv)}
            handleClick={() => handleLevelSelect(lv)}
            theme={ETheme.BROWN}
          />
        ))}
      </Row>
      <Row>
        <Label>
          <TextBlock text='발행' design={{ text: { size: 'small', color: color.BLACK_4 } }} />
        </Label>
        <Chip text='전체' size='small' isSelected={published === null} handleClick={() => handlePublishedChange(null)} theme={ETheme.BROWN} />
        <Chip text='발행됨' size='small' isSelected={published === true} handleClick={() => handlePublishedChange(true)} theme={ETheme.BROWN} />
        <Chip text='미발행' size='small' isSelected={published === false} handleClick={() => handlePublishedChange(false)} theme={ETheme.BROWN} />
      </Row>
    </Wrapper>
  );
};

export default VocabularyBookFilter;
